import {
    buildPreviewObjectUrl,
    isValidMediaDuration,
    resolveAudioDuration,
    seekAudioSafe,
    teardownPreviewAudio,
} from './audioRecorder';

export type AudioPreviewUi = {
    isPreviewPlaying: boolean;
    canResumePreview: boolean;
    previewProgress: number;
};

type PreviewHost = {
    isAlive: () => boolean;
    getElapsed: () => number;
    onPatch: (partial: Partial<AudioPreviewUi>) => void;
    onError: (message: string) => void;
};

const PREVIEW_RESET: AudioPreviewUi = {
    isPreviewPlaying: false,
    canResumePreview: false,
    previewProgress: 0,
};

/** Preview/seek do blob gravado (separado do MediaRecorder). */
export class AudioPreviewController {
    private url: string | null = null;
    private audio: HTMLAudioElement | null = null;
    private duration = 0;

    constructor(private readonly host: PreviewHost) {}

    stop(): void {
        this.clear();
        this.host.onPatch({ ...PREVIEW_RESET });
    }

    dispose(): void {
        this.clear();
    }

    prepareFromChunks(chunks: Blob[], mimeType: string): void {
        this.clear();
        const url = buildPreviewObjectUrl(chunks, mimeType);
        if (!url) return;
        this.url = url;
        this.duration = Math.max(this.host.getElapsed(), 0.001);
        this.ensureAudio();
    }

    toggle(): void {
        const existing = this.audio;
        if (existing && !existing.paused) {
            existing.pause();
            this.host.onPatch({
                isPreviewPlaying: false,
                canResumePreview: existing.currentTime > 0 && !existing.ended,
            });
            this.syncProgress(existing);
            return;
        }

        const audio = this.ensureAudio();
        if (!audio) {
            this.host.onError('Ainda não há áudio para ouvir. Grave um pouco mais.');
            return;
        }
        if (audio.ended) {
            seekAudioSafe(audio, 0);
            this.host.onPatch({ previewProgress: 0 });
        }
        void audio.play().then(() => {
            if (!this.host.isAlive()) return;
            this.host.onPatch({ isPreviewPlaying: true, canResumePreview: false });
        }).catch(() => {
            if (this.host.isAlive()) this.host.onPatch({ isPreviewPlaying: false });
            this.host.onError('Não foi possível reproduzir o áudio gravado.');
        });
    }

    seek(ratio: number): void {
        const audio = this.ensureAudio();
        if (!audio) return;
        const clamped = Math.min(1, Math.max(0, ratio));
        const duration = resolveAudioDuration(audio, this.duration, this.host.getElapsed());
        this.duration = duration;
        seekAudioSafe(audio, clamped * duration);
        if (!this.host.isAlive()) return;
        this.host.onPatch({
            previewProgress: clamped,
            canResumePreview: clamped > 0 && clamped < 1,
        });
    }

    private clear(): void {
        teardownPreviewAudio(this.audio, this.url);
        this.audio = null;
        this.url = null;
        this.duration = 0;
    }

    private syncProgress(audio: HTMLAudioElement): void {
        if (!this.host.isAlive()) return;
        const d = resolveAudioDuration(audio, this.duration, this.host.getElapsed());
        this.duration = d;
        this.host.onPatch({
            previewProgress: Math.min(1, Math.max(0, audio.currentTime / d)),
        });
    }

    private ensureAudio(): HTMLAudioElement | null {
        if (!this.url) return null;
        if (!this.audio) {
            const audio = new Audio();
            this.audio = audio;
            this.bind(audio);
            audio.preload = 'metadata';
            audio.src = this.url;
        }
        return this.audio;
    }

    private bind(audio: HTMLAudioElement): void {
        audio.ontimeupdate = () => this.syncProgress(audio);
        audio.onended = () => {
            seekAudioSafe(audio, 0);
            this.host.onPatch({ ...PREVIEW_RESET });
        };
        audio.onerror = () => {
            if (this.host.isAlive()) this.host.onPatch({ isPreviewPlaying: false });
        };
        audio.onloadedmetadata = () => {
            if (!this.host.isAlive()) return;
            this.duration = isValidMediaDuration(audio.duration)
                ? audio.duration
                : Math.max(this.host.getElapsed(), 0.001);
        };
    }
}
